/**
 * Policy record (real `/v2/policy-terms/` backend) — OR-1665, backend OR-1620.
 *
 *   GET /v2/policy-terms/{id}               header, summary and term strip
 *   GET /v2/policy-terms/{id}/facilities/   coverage rows, paged on the server
 *   GET /v2/policy-terms/{id}/premium       the term's transactions (OR-1667)
 *   GET /v2/policy-terms/{id}/folders/      Files tab folders with counts (OR-1668)
 *   GET …/folders/{folderId}/files/         a folder's files; read-only, no upload
 *   GET /v2/policy-terms/{id}/subjectivities/  the term's subjectivities (OR-1666)
 *   PATCH …/subjectivities/{attachmentId}       set one subjectivity's status
 *
 * The page asks for one term; the Policies list row hands over the current
 * one (`policy_term_id`). Wire shapes are mapped here to the screen's
 * shapes, so the tabs don't read snake_case.
 */
import { getJsonReal, sendJsonReal } from "../client";
import type { PolicyTermStatus } from "./policies";

// ── Wire ────────────────────────────────────────────────────────────────

interface PartyRefWire {
    id: string;
    name: string;
}

interface PolicyTermTabWire {
    id: string;
    term_sequence: number;
    policy_number: string;
    status: PolicyTermStatus;
    effective_date: string;
    expiration_date: string;
}

export interface PolicyTermRecordWire {
    id: string;
    policy_id: string;
    term_sequence: number;
    policy_number: string;
    named_insured: string;
    status: PolicyTermStatus;
    effective_date: string;
    expiration_date: string;
    last_amended_date: string;
    broker: PartyRefWire | null;
    brokerage: PartyRefWire | null;
    /** Decimal string. */
    premium_in_force: string;
    per_occurrence_limit: string | null;
    policy_aggregate_limit: string | null;
    declared_sir: string | null;
    /** The submission the term was bound from; null for a term with none.
     *  Optional so a backend without the field keeps "View Submission" hidden. */
    source_submission_id?: string | null;
    terms: PolicyTermTabWire[];
}

export type PolicyTermFacilityStatus = "active" | "removed";

export interface PolicyTermFacilityWire {
    id: string;
    facility_id: string;
    location_name: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    premium: string | null;
    /** ISO date the row entered coverage. */
    effective_from: string;
    /** ISO date it left coverage; null while still covered. */
    effective_to: string | null;
    /** `removed` once `effective_to` is on or before today, else `active`. */
    status: PolicyTermFacilityStatus;
}

export interface PolicyTermFacilityListWire {
    total: number;
    items: PolicyTermFacilityWire[];
}

export type PolicyTransactionType = "issuance" | "endorsement" | "cancellation" | "reinstatement" | "renewal" | "extension" | "correction";
export type PolicyTransactionStatus = "draft" | "pending" | "issued" | "voided";

interface PolicyTransactionPremiumWire {
    id: string;
    sequence: number;
    type: PolicyTransactionType;
    status: PolicyTransactionStatus;
    effective_date: string;
    endorsement_number: string;
    /** Signed decimal: the transaction's premium entries summed, taxes and fees excluded. */
    premium: string;
}

export interface PolicyTermPremiumWire {
    written_premium: string;
    /** Issue order, drafts included. */
    transactions: PolicyTransactionPremiumWire[];
}

interface PolicyTermFolderWire {
    id: string;
    name: string;
    item_count: number;
}

interface PolicyTermFolderListWire {
    total: number;
    items: PolicyTermFolderWire[];
}

interface UserDisplayWire {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
}

interface PolicyTermFileWire {
    id: string;
    name: string;
    content_type: string | null;
    uploaded_at: string | null;
    uploaded_by_user: UserDisplayWire | null;
    /** Presigned; served as an attachment, so it downloads rather than opens. */
    download_url: string | null;
}

interface PolicyTermFileListWire {
    total: number;
    items: PolicyTermFileWire[];
}

export type SubjectivityStatus = "outstanding" | "satisfied" | "waived";

interface SubjectivityAttachmentWire {
    id: string;
    title: string;
    description: string | null;
    status: SubjectivityStatus;
    due_date: string | null;
}

interface SubjectivityAttachmentListWire {
    total: number;
    items: SubjectivityAttachmentWire[];
}

// ── Screen shapes ───────────────────────────────────────────────────────

/** One policy year, for the term strip ("PY 25-26"). */
export interface PolicyTermRef {
    id: string;
    effectiveDate: string;
    expirationDate: string;
}

/** The record header and summary card for one term. */
export interface PolicyTermRecord {
    id: string;
    policyId: string;
    policyNumber: string;
    namedInsured: string;
    status: PolicyTermStatus;
    effectiveDate: string;
    expirationDate: string;
    brokerageName: string | null;
    brokerName: string | null;
    /** Decimal strings; null renders as "-". */
    premiumInForce: string | null;
    limitPerClaim: string | null;
    limitAggregate: string | null;
    sir: string | null;
    /** Null hides "View Submission". */
    sourceSubmissionId: string | null;
    /** Every term of the policy, newest first, for the term strip. */
    terms: PolicyTermRef[];
}

/** One coverage row on the Facilities tab. */
export interface TermFacility {
    id: string;
    facilityName: string;
    address: string;
    status: PolicyTermFacilityStatus;
    premium: string | null;
    /** When the facility entered coverage on this term. */
    inceptionDate: string;
    /** When it left coverage; null while still covered. */
    divestedDate: string | null;
}

/** One row on the Premium tab: an issued transaction and its signed premium. */
export interface PremiumTransaction {
    id: string;
    type: PolicyTransactionType;
    /** Second half of the row title. */
    summary: string;
    effectiveDate: string;
    /** Signed decimal string: positive money in, negative money out. */
    amount: string;
}

export interface TermPremium {
    /** Issued transactions, newest first. */
    rows: PremiumTransaction[];
    /** Bound but not issued yet: the issuance exists as a draft or pending
     *  transaction, so its premium is recorded but not in force (OR-1800). */
    pendingIssuance: boolean;
}

/** A folder card on the Files tab. */
export interface FolderSummary {
    id: string;
    name: string;
    itemCount: number;
}

/** One row in a folder. */
export interface PolicyFile {
    id: string;
    name: string;
    /** Display name; null when the file came from the system (e.g. an invoice PDF). */
    uploadedBy: string | null;
    uploadedAt: string | null;
    downloadUrl: string | null;
}

/** One row on the Subjectivities tab. */
export interface TermSubjectivity {
    id: string;
    title: string;
    /** Optional second line. */
    description: string | null;
    status: SubjectivityStatus;
}

/** Sortable Facilities columns, as the backend names them. */
export type TermFacilitySort = "location_name" | "address" | "effective_from" | "effective_to" | "premium";

export interface ListTermFacilitiesParams {
    offset?: number;
    limit?: number;
    search?: string;
    /** A `TermFacilitySort`, `-` prefix for descending. Backend default: `location_name`. */
    sort?: string;
}

// ── Mapping ─────────────────────────────────────────────────────────────

function toRecord(w: PolicyTermRecordWire): PolicyTermRecord {
    return {
        id: w.id,
        policyId: w.policy_id,
        policyNumber: w.policy_number,
        namedInsured: w.named_insured,
        status: w.status,
        effectiveDate: w.effective_date,
        expirationDate: w.expiration_date,
        brokerageName: w.brokerage?.name ?? null,
        brokerName: w.broker?.name ?? null,
        premiumInForce: w.premium_in_force,
        limitPerClaim: w.per_occurrence_limit,
        limitAggregate: w.policy_aggregate_limit,
        sir: w.declared_sir,
        sourceSubmissionId: w.source_submission_id ?? null,
        terms: [...w.terms]
            .sort((a, b) => b.effective_date.localeCompare(a.effective_date))
            .map((t) => ({ id: t.id, effectiveDate: t.effective_date, expirationDate: t.expiration_date })),
    };
}

/** Migrated rows store the whole address as one comma-joined line
 *  ("7201 W AVE,GLENDALE,AZ,85308"); rows bound in Orion store only the street,
 *  with city, state and zip in their own fields (OR-1814). Keep a full line
 *  as-is, otherwise join the street to the parts; either way, space it out. */
function addressLine(w: PolicyTermFacilityWire): string {
    const street = w.address?.trim() ?? "";
    const raw = street.includes(",") ? street : [street, w.city, w.state, w.zip_code].filter(Boolean).join(", ");
    return raw.replace(/\s*,\s*/g, ", ");
}

function toFacility(w: PolicyTermFacilityWire): TermFacility {
    return {
        id: w.id,
        facilityName: w.location_name ?? "",
        address: addressLine(w),
        status: w.status,
        premium: w.premium,
        inceptionDate: w.effective_from,
        divestedDate: w.effective_to,
    };
}

/** Issued transactions only, newest first: they are the money that moved, and
 *  they sum to the written premium. Drafts and pending ones are not money yet;
 *  voided ones never were. */
function toPremium(w: PolicyTermPremiumWire): TermPremium {
    const rows = w.transactions
        .filter((t) => t.status === "issued")
        .sort((a, b) => b.sequence - a.sequence)
        .map((t) => ({ id: t.id, type: t.type, summary: t.endorsement_number, effectiveDate: t.effective_date, amount: t.premium }));
    const pendingIssuance = w.transactions.some((t) => t.type === "issuance" && (t.status === "draft" || t.status === "pending"));
    return { rows, pendingIssuance };
}

function toFile(w: PolicyTermFileWire): PolicyFile {
    const u = w.uploaded_by_user;
    const name = u ? [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email : null;
    return { id: w.id, name: w.name, uploadedBy: name, uploadedAt: w.uploaded_at, downloadUrl: w.download_url };
}

// ── Calls ───────────────────────────────────────────────────────────────

export async function getPolicyTerm(termId: string): Promise<PolicyTermRecord> {
    return toRecord(await getJsonReal<PolicyTermRecordWire>(`/v2/policy-terms/${encodeURIComponent(termId)}`));
}

export async function listTermFacilities(termId: string, params: ListTermFacilitiesParams = {}): Promise<{ total: number; items: TermFacility[] }> {
    const qs = new URLSearchParams();
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.search) qs.set("search", params.search);
    if (params.sort) qs.set("sort", params.sort);
    const query = qs.toString();
    const page = await getJsonReal<PolicyTermFacilityListWire>(`/v2/policy-terms/${encodeURIComponent(termId)}/facilities/${query ? `?${query}` : ""}`);
    return { total: page.total, items: page.items.map(toFacility) };
}

export async function getTermPremium(termId: string): Promise<TermPremium> {
    return toPremium(await getJsonReal<PolicyTermPremiumWire>(`/v2/policy-terms/${encodeURIComponent(termId)}/premium`));
}

/** The term's folders, in display order. */
export async function listTermFolders(termId: string): Promise<FolderSummary[]> {
    const page = await getJsonReal<PolicyTermFolderListWire>(`/v2/policy-terms/${encodeURIComponent(termId)}/folders/`);
    return page.items.map((f) => ({ id: f.id, name: f.name, itemCount: f.item_count }));
}

/** Largest page the backend serves. */
const FILES_PAGE_SIZE = 200;

/** Every file in a folder, page by page, so a folder past one page still
 *  lists (and downloads) all of them. */
export async function listFolderFiles(termId: string, folderId: string): Promise<PolicyFile[]> {
    const base = `/v2/policy-terms/${encodeURIComponent(termId)}/folders/${encodeURIComponent(folderId)}/files/`;
    const files: PolicyFile[] = [];
    for (let offset = 0; ; offset += FILES_PAGE_SIZE) {
        const page = await getJsonReal<PolicyTermFileListWire>(`${base}?offset=${offset}&limit=${FILES_PAGE_SIZE}`);
        files.push(...page.items.map(toFile));
        if (page.items.length === 0 || files.length >= page.total) return files;
    }
}

function toSubjectivity(w: SubjectivityAttachmentWire): TermSubjectivity {
    return { id: w.id, title: w.title, description: w.description, status: w.status };
}

export async function listTermSubjectivities(termId: string): Promise<TermSubjectivity[]> {
    const page = await getJsonReal<SubjectivityAttachmentListWire>(`/v2/policy-terms/${encodeURIComponent(termId)}/subjectivities/`);
    return page.items.map(toSubjectivity);
}

/** Set a subjectivity's status on this term. The backend stamps who resolved
 *  it and when; moving it back to outstanding clears that. */
export async function updateTermSubjectivityStatus(termId: string, attachmentId: string, status: SubjectivityStatus): Promise<TermSubjectivity> {
    const path = `/v2/policy-terms/${encodeURIComponent(termId)}/subjectivities/${encodeURIComponent(attachmentId)}`;
    return toSubjectivity(await sendJsonReal<SubjectivityAttachmentWire>(path, "PATCH", { status }));
}
